import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Mail, KeyRound, User as UserIcon, ShieldCheck } from "lucide-react";

const ROLE_LABELS = { admin: "Yönetici", data_entry: "Veri Giriş", viewer: "Görüntüleyici" };

// Şifre politikası: min 12 karakter, en az 1 harf + 1 rakam
function validatePasswordPolicy(pw) {
  if (!pw || pw.length < 12) return "Şifre en az 12 karakter olmalı";
  if (!/[A-Za-zÇĞİÖŞÜçğıöşü]/.test(pw)) return "Şifre en az bir harf içermeli";
  if (!/\d/.test(pw)) return "Şifre en az bir rakam içermeli";
  return null;
}

export default function Profile() {
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setEmail(data.email || "");
      } catch {}
    })();
  }, []);

  const saveEmail = async () => {
    setBusy(true);
    try {
      await api.patch("/auth/me", { email });
      toast.success("E-posta güncellendi. Kritik uyarılar artık buraya gönderilebilir.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hata");
    } finally { setBusy(false); }
  };

  const savePassword = async () => {
    if (!oldPw) return toast.error("Mevcut şifrenizi girin");
    const policyErr = validatePasswordPolicy(newPw);
    if (policyErr) return toast.error(policyErr);
    if (newPw !== confirmPw) return toast.error("Yeni şifre ile tekrarı aynı olmalı");
    if (newPw === oldPw) return toast.error("Yeni şifre mevcut şifreyle aynı olamaz");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { old_password: oldPw, new_password: newPw });
      toast.success("Şifreniz değiştirildi");
      setOldPw(""); setNewPw(""); setConfirmPw("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Şifre değiştirilemedi");
    } finally { setBusy(false); }
  };

  const policyLive = newPw ? validatePasswordPolicy(newPw) : null;
  const matchOk = newPw && confirmPw && newPw === confirmPw;

  return (
    <div className="max-w-2xl space-y-6" data-testid="profile-page">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-mono">Hesabım</div>
        <h1 className="mt-1 font-heading font-bold text-3xl lg:text-4xl tracking-tight text-slate-900">Profil</h1>
        <p className="mt-2 text-sm text-slate-500">Bildirim almak istiyorsanız e-postanızı buraya girin. Şifrenizi de buradan değiştirebilirsiniz.</p>
      </div>

      <Card className="border-slate-200 shadow-none">
        <CardHeader className="border-b border-slate-100">
          <div className="flex items-center gap-2">
            <UserIcon className="w-4 h-4 text-[#1E3A8A]" />
            <CardTitle className="font-heading text-lg">Hesap Bilgileri</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="pt-6 space-y-3">
          <Row label="Ad Soyad" value={user?.name} />
          <Row label="Kullanıcı Adı" value={<span className="font-mono">@{user?.username}</span>} />
          <Row label="Rol" value={
            <span className={`text-xs px-2 py-1 rounded border ${
              user?.role === "admin" ? "bg-blue-50 text-[#1E3A8A] border-blue-200" :
              user?.role === "viewer" ? "bg-amber-50 text-amber-800 border-amber-200" :
              "bg-slate-100 text-slate-700 border-slate-200"
            }`}>{ROLE_LABELS[user?.role] || user?.role}</span>
          } />
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-none">
        <CardHeader className="border-b border-slate-100">
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-[#1E3A8A]" />
            <CardTitle className="font-heading text-lg">E-posta Bildirimleri (Opsiyonel)</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="pt-6 space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider">E-posta Adresi</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="istege bagli — kritik uyari almak icin"
              className="mt-1 font-mono"
              data-testid="profile-email-input"
            />
            <div className="text-[10px] text-slate-500 mt-1">Boş bırakırsanız bildirim gönderilmez. Giriş için kullanılmaz.</div>
          </div>
          <Button onClick={saveEmail} disabled={busy} className="bg-[#1E3A8A] hover:bg-[#1e40af]" data-testid="profile-email-save">
            E-postayı Kaydet
          </Button>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-none">
        <CardHeader className="border-b border-slate-100">
          <div className="flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-[#1E3A8A]" />
            <CardTitle className="font-heading text-lg">Şifre Değiştir</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          <div className="rounded-md border border-blue-100 bg-blue-50/50 text-[11px] text-blue-900 px-3 py-2 flex items-start gap-2">
            <ShieldCheck className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-semibold">Şifre Politikası</div>
              <ul className="list-disc ml-4 mt-0.5 space-y-0.5">
                <li>En az <strong>12 karakter</strong></li>
                <li>En az bir <strong>harf</strong> ve bir <strong>rakam</strong> içermeli</li>
                <li>Mevcut şifreden farklı olmalı</li>
              </ul>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider">Mevcut Şifre</Label>
            <Input
              type="password"
              value={oldPw}
              onChange={(e) => setOldPw(e.target.value)}
              className="mt-1"
              autoComplete="current-password"
              data-testid="profile-oldpw-input"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider">Yeni Şifre</Label>
            <Input
              type="password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              className="mt-1"
              autoComplete="new-password"
              data-testid="profile-newpw-input"
            />
            {newPw && policyLive && (
              <div className="text-[11px] text-red-600 mt-1" data-testid="profile-newpw-policy-error">{policyLive}</div>
            )}
            {newPw && !policyLive && (
              <div className="text-[11px] text-emerald-600 mt-1">Politika kurallarına uygun.</div>
            )}
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider">Yeni Şifre (Tekrar)</Label>
            <Input
              type="password"
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              className="mt-1"
              autoComplete="new-password"
              data-testid="profile-confirmpw-input"
            />
            {confirmPw && !matchOk && (
              <div className="text-[11px] text-red-600 mt-1" data-testid="profile-confirmpw-mismatch">Şifre tekrarı uyuşmuyor.</div>
            )}
          </div>

          <Button
            onClick={savePassword}
            disabled={busy || !oldPw || !!policyLive || !matchOk}
            variant="outline"
            data-testid="profile-password-save"
          >
            Şifreyi Değiştir
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-slate-100 last:border-0 py-2">
      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-mono">{label}</div>
      <div className="text-sm font-medium">{value}</div>
    </div>
  );
}
